import React from 'react';
import { Modal, Table, Typography, Divider, Row, Col, Button, Space } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const SalesOrderPrintModal = ({ visible, onCancel, data }) => {
    if (!data) return null;

    const handlePrint = () => {
        const printArea = document.getElementById('sales-order-print-area');
        if (!printArea) {
            return;
        }

        const body = document.body;
        const printClass = 'print-sales-order';

        const cleanup = () => {
            body.classList.remove(printClass);
            window.removeEventListener('afterprint', cleanup);
        };

        window.addEventListener('afterprint', cleanup);
        body.classList.add(printClass);
        window.print();
        setTimeout(cleanup, 1000);
    };

    const items = data.items || [];
    const subtotal = items.reduce((sum, item) => sum + Number(item.line_total || 0), 0);
    const discount = Number(data.discount || 0);
    const total = Number(data.total_amount || subtotal - discount);

    const columns = [
        { title: 'Item', dataIndex: ['item', 'name'], key: 'item' },
        { title: 'Category', dataIndex: ['item', 'category', 'name'], key: 'category' },
        { title: 'Lot', dataIndex: ['lot', 'lot_number'], key: 'lot' },
        { title: 'Qty', dataIndex: 'quantity', key: 'qty', align: 'right', render: (val) => Number(val).toLocaleString() },
        { title: 'Unit Price', dataIndex: 'unit_price', key: 'unit_price', align: 'right', render: (val) => `₱${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
        { title: 'Line Total', dataIndex: 'line_total', key: 'line_total', align: 'right', render: (val) => `₱${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
    ];

    return (
        <Modal
            open={visible}
            onCancel={onCancel}
            width={900}
            footer={[
                <Button key="back" onClick={onCancel}>
                    Close
                </Button>,
                <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
                    Print Sales Order
                </Button>,
            ]}
            title="Sales Order"
        >
            <div id="sales-order-print-area" style={{ padding: '20px', backgroundColor: '#fff', color: '#000' }}>
                <div style={{ textAlign: 'center', marginBottom: 30 }}>
                    <Title level={3} style={{ margin: 0, textTransform: 'uppercase' }}>BMPC Agri-Inventory System</Title>
                    <Text type="secondary">Sales Order</Text>
                </div>

                <Row gutter={[24, 12]} style={{ marginBottom: 24 }}>
                    <Col span={12}>
                        <Space direction="vertical" size={0}>
                            <Text strong type="secondary" style={{ fontSize: 10, textTransform: 'uppercase' }}>Customer</Text>
                            <Text strong style={{ fontSize: 16 }}>{data.customer?.name || data.customer_name || 'Walk-in'}</Text>
                        </Space>
                    </Col>
                    <Col span={12} style={{ textAlign: 'right' }}>
                        <Space direction="vertical" size={0}>
                            <Text strong type="secondary" style={{ fontSize: 10, textTransform: 'uppercase' }}>Order No.</Text>
                            <Text strong style={{ fontSize: 16 }}>{data.order_number}</Text>
                        </Space>
                    </Col>
                    <Col span={12}>
                        <Space direction="vertical" size={0}>
                            <Text strong type="secondary" style={{ fontSize: 10, textTransform: 'uppercase' }}>Reference No.</Text>
                            <Text>{data.reference_number || 'N/A'}</Text>
                        </Space>
                    </Col>
                    <Col span={12}>
                        <Space direction="vertical" size={0}>
                            <Text strong type="secondary" style={{ fontSize: 10, textTransform: 'uppercase' }}>Order Date</Text>
                            <Text>{dayjs(data.order_date).format('MMMM DD, YYYY HH:mm')}</Text>
                        </Space>
                    </Col>
                    <Col span={12} style={{ textAlign: 'right' }}>
                        <Space direction="vertical" size={0}>
                            <Text strong type="secondary" style={{ fontSize: 10, textTransform: 'uppercase' }}>Branch</Text>
                            <Text>{data.branch?.name || 'N/A'}</Text>
                        </Space>
                    </Col>
                </Row>

                <Divider style={{ margin: '12px 0' }} />

                <Table
                    dataSource={items}
                    columns={columns}
                    pagination={false}
                    rowKey="id"
                    bordered
                    size="small"
                    summary={() => (
                        <>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={4} align="right">Subtotal</Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right">₱{subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Table.Summary.Cell>
                            </Table.Summary.Row>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={4} align="right">Discount</Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right">₱{discount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Table.Summary.Cell>
                            </Table.Summary.Row>
                            <Table.Summary.Row style={{ fontWeight: 'bold' }}>
                                <Table.Summary.Cell index={0} colSpan={4} align="right">Total</Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right">₱{total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Table.Summary.Cell>
                            </Table.Summary.Row>
                        </>
                    )}
                />

                {data.rice_allowance_batch && (
                    <>
                        <Divider>Rice Allowance Employees</Divider>
                        <Table
                            dataSource={data.rice_allowance_batch.recipients || []}
                            pagination={false}
                            rowKey="id"
                            bordered
                            size="small"
                            columns={[
                                { title: 'Employee', render: (_, row) => row.employee?.name || 'Unknown' },
                                { title: 'Rice Item', render: (_, row) => row.item?.name || 'N/A' },
                                { title: 'KG', dataIndex: 'allowance_kg', align: 'right', render: value => Number(value).toLocaleString() },
                                { title: 'Price', dataIndex: 'unit_price', align: 'right', render: value => `₱${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
                                { title: 'Amount', dataIndex: 'line_total', align: 'right', render: value => `₱${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
                            ]}
                        />
                    </>
                )}

                <Row gutter={48} style={{ marginTop: 60, textAlign: 'center' }}>
                    <Col span={8}>
                        <div style={{ borderTop: '1px solid #000', paddingTop: 8 }}>
                            <Text strong style={{ display: 'block' }}>Prepared By</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>{data.creator?.name || 'Encoder'}</Text>
                        </div>
                    </Col>
                    <Col span={8}>
                        <div style={{ borderTop: '1px solid #000', paddingTop: 8 }}>
                            <Text strong style={{ display: 'block' }}>Reviewed By</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>Manager / Accountant</Text>
                        </div>
                    </Col>
                    <Col span={8}>
                        <div style={{ borderTop: '1px solid #000', paddingTop: 8 }}>
                            <Text strong style={{ display: 'block' }}>Approved By</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>Authorized Signatory</Text>
                        </div>
                    </Col>
                </Row>

                <style>
                    {`
                        @media print {
                            .ant-modal-close, .ant-modal-footer, .no-print {
                                display: none !important;
                            }
                            body {
                                padding: 0 !important;
                                margin: 0 !important;
                            }
                            body.print-sales-order * {
                                visibility: hidden !important;
                            }
                            body.print-sales-order #sales-order-print-area,
                            body.print-sales-order #sales-order-print-area * {
                                visibility: visible !important;
                            }
                            #sales-order-print-area {
                                width: 100% !important;
                                position: absolute !important;
                                left: 0 !important;
                                top: 0 !important;
                                padding: 40px !important;
                            }
                        }
                    `}
                </style>
            </div>
        </Modal>
    );
};

export default SalesOrderPrintModal;


